import { useCallback, useEffect, useMemo } from 'react';

import { useMutationScheduleUpdate, useQueryUserProfile } from '@hooks/index';
import { ISchedule, IScheduleDto } from '@interfaces/scheduleInterface';
import { IRole, IUser, Role } from '@interfaces/userInterface';
import { Button, message, Popconfirm } from 'antd';
import dayjs from 'dayjs';

import ScheduleReplace from '../ScheduleReplace/ScheduleReplace';

const ROLE_KEYS = Object.values(Role) as Role[];

const RESTRICTED_ROLES_BY_SCHEDULE_TYPE: Record<string, Role[]> = {
	SCO: [Role.Controller, Role.Store]
};

type ScheduleUserProps = {
	user: IUser;
	schedule: ISchedule;
	role: Role;
};

const ScheduleUser = ({ user, schedule, role }: ScheduleUserProps) => {
	const { data: userProfile } = useQueryUserProfile();
	const { mutate: mutateUpdateSchedule, isSuccess: isSuccessMutateUpdateSchedule } = useMutationScheduleUpdate();

	const isAlreadySubscribed = useMemo(() => {
		if (!userProfile) return false;

		return ROLE_KEYS.some((key) => schedule[key]?.id === userProfile.id);
	}, [userProfile, schedule]);

	const firstAssignedKey = useMemo(() => {
		if (!userProfile) return null;
		return ROLE_KEYS.find((key) => schedule[key]?.id === userProfile.id) ?? null;
	}, [userProfile, schedule]);

	const onSubscribeUser = useCallback(
		(roleToCheck: Role | undefined, userId: number) => {
			if (isAlreadySubscribed) {
				return;
			}

			// Build a properly typed IScheduleDto from the current schedule
			const scheduleDtoBase: IScheduleDto = {
				id: schedule.id,
				date: schedule.date,
				type: schedule.type,
				createdAt: schedule.createdAt,
				updatedAt: schedule.updatedAt,
				deletedAt: schedule.deletedAt,
				responsable: schedule.responsable ? schedule.responsable.id : null,
				cashier: schedule.cashier ? schedule.cashier.id : null,
				controller: schedule.controller ? schedule.controller.id : null,
				store: schedule.store ? schedule.store.id : null,
				operator: schedule.operator ? schedule.operator.id : null,
				glasses: schedule.glasses ? schedule.glasses.id : null,
				movie: schedule.movie
			};

			// Use the provided role (or the checked role) as the target
			const targetKey: Role = (roleToCheck ?? role) as Role;

			const scheduleDto: IScheduleDto = {
				...scheduleDtoBase,
				[targetKey]: userId
			} as IScheduleDto;

			mutateUpdateSchedule({ scheduleId: schedule.id, scheduleDto });
		},
		[schedule, mutateUpdateSchedule, isAlreadySubscribed, role]
	);

	const validateUserRole = (roles: IRole[] = [], roleToCheck: string | Role): boolean => {
		// If the target role is Glasses, allow any authenticated user to register
		if (roleToCheck === Role.Glasses) return true;
		return roles.some((roleItem) => roleItem.name === roleToCheck);
	};

	const userTakeThisSchedule = useMemo(() => {
		if (!userProfile || !schedule) return false;
		return !!(schedule[role] && schedule[role].id === userProfile.id);
	}, [userProfile, schedule, role]);

	const isPastSchedule = useMemo(() => dayjs().isAfter(dayjs(schedule.date)), [schedule.date]);

	const canSubscribe = useMemo(
		() => !schedule.type.some((type) => RESTRICTED_ROLES_BY_SCHEDULE_TYPE[type]?.includes(role)),
		[schedule.type, role]
	);

	useEffect(() => {
		if (isSuccessMutateUpdateSchedule) {
			message.success('Vous êtes correctement inscrit');
		}
	}, [isSuccessMutateUpdateSchedule]);

	return (
		<div className="min-h-[32px]">
			{canSubscribe &&
				!user &&
				userProfile &&
				validateUserRole(userProfile.roles, role) &&
				!isPastSchedule &&
				!isAlreadySubscribed && (
					<Popconfirm
						title="Inscription"
						description="Confirmez-vous l'inscription?"
						okText="Oui"
						cancelText="Non"
						onConfirm={() => onSubscribeUser(role, userProfile.id)}
					>
						<Button size="small" className="" type="primary">
							S'inscrire
						</Button>
					</Popconfirm>
				)}
			{user && (
				<div className="text-sm">
					{user.firstName} {user.lastName}
				</div>
			)}

			{userProfile &&
				validateUserRole(userProfile.roles, role) &&
				userTakeThisSchedule &&
				firstAssignedKey !== null &&
				firstAssignedKey === role && (
					<ScheduleReplace
						userSuggester={userProfile.id}
						role={firstAssignedKey as Role}
						scheduleId={schedule.id}
					/>
				)}
		</div>
	);
};

export default ScheduleUser;
